import React, { useReducer, useState, useEffect } from 'react'
import { Link, useParams, useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import axios from '~/data/http/axios'
import CheckBox from '~/Common-Objects/UI/Forms/CheckBox/CheckBox'
import FormError from '~/Common-Objects/UI/Forms/FormError/FormError'
import Icon from '~/Common-Objects/UI/Icon'
import Icons from '~/Common-Objects/UI/Icons/index'
import NewUserForm from '~/Common-Objects/NewUserForm/newUserForm.jsx'
import Paper from '~/Common-Objects/UI/Paper/Paper'
import TierLimitHolder from '~/Common-Objects/UI/Cards/TierLimitHolder'
import UilibButton from '~/Common-Objects/UI/Forms/Button/Button'
import UilibTextInput from '~/Common-Objects/UI/Forms/TextInput/TextInput'
import WizardStep from '~/Common-Objects/UI/Wizard/Step'
import { wizardStepsReducer, initialWizardStepsState } from '~/Classes/wizard'
import { alterSiteDrawer, alterSnackbar, handleGenericError } from '~/data/actions/siteActions'

import copy from 'copy-to-clipboard'
import i18n from '~/i18n'
import Button from '../../components/button'
import * as styles from './index.module.scss'
import TextInput from '../../components/textInput'
import { canManageInboxSettings } from '~/Classes/conversations'

export default function ConnectWebchatChannelPage({ userRole = null }) {
    const { siteMaster } = useSelector((state) => state)
    const { inboxId } = useParams()
    const history = useHistory()
    const dispatch = useDispatch()
    const accountMaster = useSelector((state) => state.accountMaster)
    const accountUser = useSelector((state) => state.user)
    const [selectedTrackers, setSelectedTrackers] = useState([])

    const [base64id, setBase64id] = useState('')
    const [addingNewWebsite, setAddingNewWebsite] = useState(false)
    const [savingNewWebsite, setSavingNewWebsite] = useState(false)
    const [newWebsiteName, setNewWebsiteName] = useState('')
    const [newWebsiteNameError, setNewWebsiteNameError] = useState('')
    const [channelname, setChannelName] = useState('')
    const [tierData, setTierData] = useState({})
    const [loadingTierData, setLoadingTierData] = useState(true)
    const [approveDrawer, setApproveDrawer] = useState(null)
    const [steps, updateSteps] = useReducer(wizardStepsReducer, initialWizardStepsState(2))

    const [trackers, updateTrackers] = useReducer(
        (state, action) => {
            return { ...state, ...action }
        },
        {
            loading: true,
            data: []
        }
    )

    const [formErrors, updateFormErrors] = useReducer(
        (state, action) => {
            return { ...state, ...action }
        },
        {
            trackers: null
        }
    )

    useEffect(() => {
        if (!canManageInboxSettings(userRole, accountUser.userData.roleId)) {
            return window.open('/', '_self')
        }
        loadInitialData()
    }, [])

    async function loadInitialData() {
        try {
            await Promise.all([getBase64id(), getTrackers(), checkTier()])
        } catch (e) {
            console.log(e)
        }
    }

    async function getBase64id() {
        try {
            const { data } = await axios.get('/accountMaster')
            setBase64id(data.AccountMaster.base64id)
        } catch (error) {
            dispatch(handleGenericError(error))
            throw error
        }
    }
    async function getTrackers() {
        try {
            updateTrackers({ loading: true })
            const { data } = await axios.get('/accountMaster/tracker')
            updateTrackers({ data: data.Trackers })
        } catch (error) {
            dispatch(handleGenericError(error))
            throw error
        } finally {
            updateTrackers({ loading: false })
        }
    }

    async function checkTier() {
        setLoadingTierData(true)
        const { data } = await axios.get('/accountMaster/tier/webTracking')
        setTierData(data)
        setLoadingTierData(false)
    }

    function handleTrackerChange(id) {
        if (selectedTrackers.includes(id)) {
            setSelectedTrackers((prev) => prev.filter((tracker) => tracker !== id))
        } else {
            setSelectedTrackers((prev) => [...prev, id])
        }
    }
    async function handleStep1Submit(e) {
        if (e) e.preventDefault()
        try {
            await validateStep1Form()
            updateSteps({ type: 'complete', index: 0 })
        } catch (error) {
            setSaving(false)
        }
    }
    async function validateStep1Form() {
        const inputs = await Promise.allSettled([validateStep1()])
        const hasInvalidInput = inputs.some((input) => input.status === 'rejected')

        if (hasInvalidInput) throw new Error(i18n.t('chat:channel.connect.chat.step2.invalid'))
    }
    async function validateStep1() {
        try {
            if (selectedTrackers.length === 0) {
                throw i18n.t('chat:channel.connect.chat.step2.errRequired')
            }
            updateFormErrors({ trackers: null })
        } catch (error) {
            updateFormErrors({ trackers: error })
            throw error
        }
    }
    async function handleStep2Submit(e) {
        e.preventDefault()
        try {
            if (!channelname || channelname.trim().length < 4) {
                throw i18n.t('chat:channel.connect.chat.step3.required')
            }
            if (channelname.length > 100) {
                throw i18n.t('chat:channel.connect.chat.step3.maxlength')
            }
            updateSteps({ type: 'complete', index: 2 })
            const newChannel = await connectChannel()
            history.push(`/cp/conversations/${inboxId}/channels/${newChannel.id}`)
        } catch (error) {
            let errMsg = 'There was a problem saving, please try again'
            if (error?.data?.error) errMsg = error.data.error
            updateFormErrors({ channelName: errMsg })
        }
    }
    async function connectChannel() {
        try {
            const { data } = await axios.post(`/inboxes/${inboxId}/channel`, {
                name: channelname,
                status: 'draft',
                type: 2,
                siteTrackers: selectedTrackers
            })
            return data
        } catch (error) {
            dispatch(handleGenericError(error))
            throw error
        }
    }
    function copyToClipboard(string) {
        copy(string)
        dispatch(alterSnackbar(true, 'Copied to clipboard'))
    }
    const saveNewWebsite = async () => {
        try {
            setNewWebsiteNameError('')
            let tmpNewWebsiteName = newWebsiteName
                .toLowerCase()
                .replace('https://', '')
                .replace('http://', '')
                .replace('www.', '')
                .trim('')
            if (!tmpNewWebsiteName || tmpNewWebsiteName.length < 5) {
                setNewWebsiteNameError('Invalid URL')
                return
            }

            if (tmpNewWebsiteName.indexOf('.') < 0 && tmpNewWebsiteName.indexOf(':') < 0) {
                setNewWebsiteNameError('Invalid URL')
                return
            }

            if (tmpNewWebsiteName.length > 254) {
                setNewWebsiteNameError('URL is too long')
                return
            }

            setSavingNewWebsite(true)
            const resp = await axios.post('/accountMaster/tracker', { host: tmpNewWebsiteName })
            setSavingNewWebsite(false)
            setAddingNewWebsite(false)
            setNewWebsiteName('')
            setSelectedTrackers((prev) => [...prev, resp.data.id])
            getTrackers()
            checkTier()
            updateSteps({ type: 'complete', index: 0 })
        } catch (e) {
            setNewWebsiteNameError(e?.data?.error || 'Error adding site')
            setSavingNewWebsite(false)
        }
    }

    const newUserQuestions = async () => {
        var theDrawer = (
            <NewUserForm
                from='campaign'
                finish={() => {
                    setApproveDrawer(null)
                }}
            />
        )
        setApproveDrawer(theDrawer)
    }

    const embedCode = `<script src="https://${siteMaster.trackingDomain}/tracker?am=${base64id}"></script>`

    return (
        <div className='wizard-page connect-webchat-channel-page'>
            <a onClick={() => history.goBack()} className='wizard-page__close'>
                <Icons icon='cross' color='black' />
            </a>

            <div className='wizard-page__inner'>
                <div className='center-xs mar-b60'>
                    <h1 className='h2 mar-b10'>{i18n.t('chat:channel.connect.chat.header')}</h1>
                    <p className='text-md text-grey'>{i18n.t('chat:channel.connect.chat.subheader')}</p>
                </div>

                {!accountMaster.accountMaster.approved && (
                    <>
                        {accountMaster.accountMaster.doneNewUserQuestions && (
                            <WizardStep
                                heading={i18n.t('chat:channel.connect.email.approval.awaitingApproval')}
                                onClick={() => {}}
                            >
                                {i18n.t('chat:channel.connect.email.approval.awaitingApprovalDesc')}
                            </WizardStep>
                        )}
                        {!accountMaster.accountMaster.doneNewUserQuestions && (
                            <WizardStep
                                heading={i18n.t('chat:channel.connect.email.approval.youreNew')}
                                onClick={() => {}}
                            >
                                <div className='mar-b20'>
                                    {i18n.t('chat:channel.connect.email.approval.yourNewDesc')}
                                </div>
                                <UilibButton onClick={newUserQuestions}>Get Started</UilibButton>
                            </WizardStep>
                        )}
                    </>
                )}

                {accountMaster.accountMaster.approved && (
                    <>
                        <ol className='faux-list'>
                            <li>
                                <WizardStep
                                    heading={i18n.t('chat:channel.connect.chat.step1.websites')}
                                    onClick={() => updateSteps({ type: 'collapse', index: 0 })}
                                    {...steps[0]}
                                >
                                    {trackers && trackers.data?.length > 0 && (
                                        <>
                                            <div className='mar-t18'>
                                                <div className='text-grey mar-b25'>
                                                    {i18n.t('chat:channel.connect.chat.step1.selectWebsites')}
                                                </div>
                                                <div className='mar-b25 connect-webchat-channel-page__trackers'>
                                                    {trackers.data.map((tracker) => {
                                                        const { id, host, InboxChannelId: inboxChannelId } = tracker
                                                        const webchatAlreadyEnabled = inboxChannelId !== null
                                                        return (
                                                            <CheckBox
                                                                key={id}
                                                                name='access'
                                                                value={id}
                                                                checked={selectedTrackers.includes(id)}
                                                                disabled={webchatAlreadyEnabled}
                                                                onChange={() => handleTrackerChange(id)}
                                                            >
                                                                {host}
                                                                {webchatAlreadyEnabled && (
                                                                    <span className='fw-normal'>
                                                                        &nbsp;
                                                                        {i18n.t(
                                                                            'chat:channel.connect.chat.step2.alreadyEnabled'
                                                                        )}
                                                                    </span>
                                                                )}
                                                            </CheckBox>
                                                        )
                                                    })}
                                                </div>
                                                {formErrors.trackers && (
                                                    <FormError className='mar-b25'>
                                                        {formErrors.trackers}
                                                    </FormError>
                                                )}
                                                <div style={{ display: 'flex' }} className='mar-b25'>
                                                    <Button
                                                        className='mar-r10'
                                                        variant='primary'
                                                        size='s'
                                                        iconRight={<Icon name='arrow-right' />}
                                                        onClick={handleStep1Submit}
                                                    >
                                                        {i18n.t('chat:channel.connect.chat.step1.saveAndContinue')}
                                                    </Button>
                                                    <Button
                                                        variant='secondary'
                                                        size='s'
                                                        onClick={() => {
                                                            setAddingNewWebsite(true)
                                                        }}
                                                    >
                                                        {i18n.t('chat:channel.connect.chat.step1.websiteNotListed')}
                                                    </Button>
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {(addingNewWebsite || !trackers?.data?.length) && (
                                        <>
                                            {!loadingTierData && tierData.allowed && (
                                                <div
                                                    className={
                                                        addingNewWebsite
                                                            ? styles.teaser + ' ' + styles.teaser_border + ' mar-b25'
                                                            : styles.teaser + ' no-pad mar-b25'
                                                    }
                                                >
                                                    {addingNewWebsite && (
                                                        <h4 className='mar-b5'>
                                                            {i18n.t('chat:channel.connect.chat.step1.addAWebsite')}
                                                        </h4>
                                                    )}
                                                    <div className='text-grey'>
                                                        {i18n.t('chat:channel.connect.chat.step1.addAWebsiteDesc')}
                                                    </div>
                                                    <div className='text-heavy mar-b0'>
                                                        {i18n.t('chat:channel.connect.chat.step1.trackingCode')}
                                                    </div>
                                                    <div
                                                        className='width-100'
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            gap: 8
                                                        }}
                                                    >
                                                        <UilibTextInput
                                                            className='txtInput-greyedOut'
                                                            outerClassName='width-100'
                                                            style={{ padding: 6 }}
                                                            value={embedCode}
                                                            readOnly={true}
                                                        />
                                                        <Button
                                                            variant='secondary'
                                                            size='s'
                                                            onClick={() => {
                                                                copyToClipboard(embedCode)
                                                            }}
                                                        >
                                                            {'Copy'}
                                                        </Button>
                                                    </div>

                                                    <div className='text-heavy mar-b0'>
                                                        {i18n.t('chat:channel.connect.chat.step1.websiteAddress')}
                                                    </div>
                                                    <UilibTextInput
                                                        style={{ padding: 6 }}
                                                        value={newWebsiteName}
                                                        onChange={(ev) => {
                                                            setNewWebsiteName(ev.target.value)
                                                        }}
                                                        outerClassName='width-100'
                                                        placeholder='mywebsite.com'
                                                        error={newWebsiteNameError}
                                                    />

                                                    <div style={{ display: 'flex' }}>
                                                        <Button
                                                            disabled={savingNewWebsite}
                                                            className='mar-r10'
                                                            variant='primary'
                                                            size='s'
                                                            iconRight={<Icon name='arrow-right' />}
                                                            onClick={saveNewWebsite}
                                                        >
                                                            {i18n.t('chat:channel.connect.chat.step1.saveAndContinue')}
                                                        </Button>
                                                        <Button
                                                            disabled={savingNewWebsite}
                                                            variant='secondary'
                                                            size='s'
                                                            onClick={() => {
                                                                setAddingNewWebsite(false)
                                                            }}
                                                        >
                                                            {i18n.t('chat:channel.connect.chat.step1.cancel')}
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}
                                            {!loadingTierData && !tierData.allowed && (
                                                <>
                                                    <TierLimitHolder
                                                        type='Site Tracker'
                                                        err={tierData.err}
                                                        tierName={tierData.tierName}
                                                        allowedNumber={tierData.allowedNumber}
                                                        secondaryButton={
                                                            <UilibButton
                                                                className='mar-l10'
                                                                text={i18n.t(
                                                                    'chat:channel.connect.chat.step1.archiveOldDomains'
                                                                )}
                                                                onClick={() => {
                                                                    window.open('/cp/trackers', '_blank')
                                                                }}
                                                            />
                                                        }
                                                    />
                                                </>
                                            )}
                                        </>
                                    )}

                                    <Paper className='paper-information'>
                                        <div
                                            dangerouslySetInnerHTML={{
                                                __html: i18n.t('chat:channel.connect.chat.step1.trackingInfo')
                                            }}
                                        />
                                    </Paper>
                                </WizardStep>
                            </li>
                            <li>
                                <WizardStep
                                    heading={i18n.t('chat:channel.connect.chat.step2.details')}
                                    onClick={() => updateSteps({ type: 'collapse', index: 1 })}
                                    {...steps[1]}
                                >
                                    <div className='mar-t15'>
                                        {i18n.t('chat:channel.connect.chat.step2.header')}

                                        <UilibTextInput
                                            maxlength='100'
                                            className='mar-t15'
                                            value={channelname}
                                            onChange={(ev) => {
                                                setChannelName(ev.target.value)
                                            }}
                                            error={formErrors.channelName}
                                        />
                                    </div>
                                    <UilibButton className='button-primary mar-t18' onClick={handleStep2Submit}>
                                        {i18n.t('chat:channel.connect.chat.step3.continue')}
                                    </UilibButton>
                                </WizardStep>
                            </li>
                        </ol>
                    </>
                )}
            </div>
            {approveDrawer}
        </div>
    )
}
